import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, CircularProgress, IconButton, Select, Typography } from '@mui/material';
import {
  ArrowIcon,
  CancelIcon,
  MicOffIcon,
  UsbIcon,
  WarningTriangleFilledIcon,
} from '@sharedCR/coreDesignSystem/icons';
import telnyxService from 'app/services/telnyx/telnyxService';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getAudioRecordingsListAction } from 'store/audioRecordings/actions';
import { selectVoiceMailAudioRecordings } from 'store/audioRecordings/selectors/audioRecordings.selectors';
import {
  fetchActiveGreetingAction,
  removeActiveGreetingAction,
  setActiveGreetingAction,
  setAudioPermissionStateAction,
  setAvailableAudioDevicesAction,
  setSelectedInputDeviceAction,
  setSelectedOutputDeviceAction,
} from 'store/system/actions';
import {
  selectActiveGreetingId,
  selectAudioPermissionState,
  selectAvailableInputDevices,
  selectAvailableOutputDevices,
  selectIsGreetingLoading,
  selectIsGreetingSaving,
  selectSelectedInputDeviceId,
  selectSelectedOutputDeviceId,
} from 'store/system/selectors';

import {
  backButtonStyles,
  deviceChangeToastCloseStyles,
  deviceChangeToastIconStyles,
  deviceChangeToastStyles,
  deviceChangeToastTextStyles,
  deviceLabelStyles,
  deviceSectionStyles,
  disabledSelectStyles,
  fallbackIconStyles,
  fallbackNoticeStyles,
  fallbackTextStyles,
  greetingManageLinkStyles,
  greetingNoneOptionStyles,
  inlineDeniedIconStyles,
  inlineDeniedStyles,
  inlineDeniedTextStyles,
  inlinePermissionButtonStyles,
  inlinePermissionStyles,
  inlinePermissionTextStyles,
  loadingContainerStyles,
  loadingTextStyles,
  micLevelBarInnerStyles,
  micLevelBarOuterStyles,
  micLevelContainerStyles,
  micLevelLabelStyles,
  selectStyles,
  settingsBodyStyles,
  settingsContainerStyles,
  settingsHeaderStyles,
  settingsTitleStyles,
  testButtonStyles,
} from './AudioDeviceSettings.styles';
import { AudioDeviceSettingsTestIds } from './AudioDeviceSettings.testIds';
import { getPersistedAudioSettings, persistAudioSettings } from './audioDeviceSettings.utils';

const SYSTEM_DEFAULT_DEVICE_ID = 'default';
const SYSTEM_DEFAULT_LABEL = 'System Default';
const DEVICE_ID_PREVIEW_LENGTH = 8;
const TEST_TONE_BASE_FREQUENCY = 440;
const TEST_TONE_CHORD_FREQUENCY = 554;
const TEST_TONE_GAIN = 0.15;
const TOAST_AUTO_DISMISS_MS = 4000;
const MIC_LEVEL_POLL_INTERVAL_MS = 80;
const PERCENTAGE_MULTIPLIER = 100;
const MIC_LEVEL_NORMALIZER = 128;
const FFT_SIZE = 256;

const isRealDevice = d => d.deviceId && d.deviceId !== SYSTEM_DEFAULT_DEVICE_ID;

const enumerateDevices = async () => {
  const devices = await navigator.mediaDevices.enumerateDevices();

  const inputDevices = devices
    .filter(d => d.kind === 'audioinput' && isRealDevice(d))
    .map(d => ({
      deviceId: d.deviceId,
      label: d.label || `Microphone (${d.deviceId.slice(0, DEVICE_ID_PREVIEW_LENGTH)})`,
    }));

  const outputDevices = devices
    .filter(d => d.kind === 'audiooutput' && isRealDevice(d))
    .map(d => ({
      deviceId: d.deviceId,
      label: d.label || `Speaker (${d.deviceId.slice(0, DEVICE_ID_PREVIEW_LENGTH)})`,
    }));

  return { inputDevices, outputDevices };
};

const requestMicrophonePermission = async dispatch => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    stream.getTracks().forEach(track => track.stop());
    dispatch(setAudioPermissionStateAction('granted'));

    const { inputDevices, outputDevices } = await enumerateDevices();

    dispatch(setAvailableAudioDevicesAction({ inputDevices, outputDevices }));
  } catch {
    dispatch(setAudioPermissionStateAction('denied'));
  }
};

// #region Sub-components

const DeviceSelect = memo(({ value, devices, onChange, disabled }) => {
  const handleChange = useCallback(
    e => {
      onChange(e.target.value);
    },
    [onChange],
  );

  return (
    <Select
      native
      data-testid={AudioDeviceSettingsTestIds.DEVICE_SELECT}
      disabled={disabled}
      size="small"
      sx={disabled ? disabledSelectStyles : selectStyles}
      value={value ?? SYSTEM_DEFAULT_DEVICE_ID}
      onChange={handleChange}
    >
      <option value={SYSTEM_DEFAULT_DEVICE_ID}>{SYSTEM_DEFAULT_LABEL}</option>
      {devices.map(device => (
        <option key={device.deviceId} value={device.deviceId}>
          {device.label}
        </option>
      ))}
    </Select>
  );
});

DeviceSelect.displayName = 'DeviceSelect';

DeviceSelect.propTypes = {
  devices: PropTypes.arrayOf(
    PropTypes.shape({
      deviceId: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.string,
};

const FallbackNotice = memo(() => (
  <Box sx={fallbackNoticeStyles}>
    <WarningTriangleFilledIcon size={IconSize[18]} style={fallbackIconStyles} />
    <Typography sx={fallbackTextStyles} variant="regular12">
      Previously selected device is unavailable. Using system default.
    </Typography>
  </Box>
));

FallbackNotice.displayName = 'FallbackNotice';

const InlinePermissionPrompt = memo(({ onRequest }) => (
  <Box sx={inlinePermissionStyles}>
    <Typography sx={inlinePermissionTextStyles} variant="regular12">
      Microphone permission is needed to list input devices.
    </Typography>
    <Button
      data-testid={AudioDeviceSettingsTestIds.GRANT_ACCESS}
      size="small"
      sx={inlinePermissionButtonStyles}
      variant="contained"
      onClick={onRequest}
    >
      Grant Access
    </Button>
  </Box>
));

InlinePermissionPrompt.displayName = 'InlinePermissionPrompt';

InlinePermissionPrompt.propTypes = {
  onRequest: PropTypes.func.isRequired,
};

const InlineDeniedNotice = memo(() => (
  <Box sx={inlineDeniedStyles}>
    <MicOffIcon size={IconSize[16]} style={inlineDeniedIconStyles} />
    <Typography sx={inlineDeniedTextStyles} variant="regular12">
      Microphone access denied. Enable it in browser settings.
    </Typography>
  </Box>
));

InlineDeniedNotice.displayName = 'InlineDeniedNotice';

const MicLevelIndicator = memo(({ deviceId }) => {
  const [level, setLevel] = useState(0);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const cancelRef = { current: false };

    const startMonitoring = async () => {
      const ctxRef = { current: null };

      try {
        // When deviceId is null/undefined, use the system default mic
        const audioConstraint = deviceId ? { deviceId: { exact: deviceId } } : true;
        const constraints = { audio: audioConstraint };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        if (cancelRef.current) {
          stream.getTracks().forEach(t => t.stop());

          return;
        }

        streamRef.current = stream;

        ctxRef.current = new AudioContext();
        const source = ctxRef.current.createMediaStreamSource(stream);
        const analyser = ctxRef.current.createAnalyser();

        analyser.fftSize = FFT_SIZE;
        source.connect(analyser);
        analyserRef.current = { analyser, ctx: ctxRef.current };

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        intervalRef.current = setInterval(() => {
          analyser.getByteFrequencyData(dataArray);

          const avg = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
          const normalized = Math.min(
            PERCENTAGE_MULTIPLIER,
            (avg / MIC_LEVEL_NORMALIZER) * PERCENTAGE_MULTIPLIER,
          );

          setLevel(normalized);
        }, MIC_LEVEL_POLL_INTERVAL_MS);
      } catch {
        // If AudioContext was created but a later operation failed, close it
        // to prevent a resource leak (analyserRef may not have been set yet).
        if (ctxRef.current && !analyserRef.current) {
          ctxRef.current.close();
        }

        setLevel(0);
      }
    };

    startMonitoring();

    return () => {
      cancelRef.current = true;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }

      if (analyserRef.current) {
        analyserRef.current.ctx.close();
        analyserRef.current = null;
      }

      setLevel(0);
    };
  }, [deviceId]);

  const barWidth = useMemo(() => ({ width: `${level}%` }), [level]);
  const innerStyles = useMemo(() => ({ ...micLevelBarInnerStyles, ...barWidth }), [barWidth]);

  return (
    <Box sx={micLevelContainerStyles}>
      <Typography sx={micLevelLabelStyles} variant="regular12">
        Level
      </Typography>
      <Box sx={micLevelBarOuterStyles}>
        <Box sx={innerStyles} />
      </Box>
    </Box>
  );
});

MicLevelIndicator.displayName = 'MicLevelIndicator';

MicLevelIndicator.propTypes = {
  deviceId: PropTypes.string,
};

const DeviceChangeToast = memo(({ message, onClose }) => (
  <Box sx={deviceChangeToastStyles}>
    <UsbIcon size={IconSize[16]} style={deviceChangeToastIconStyles} />
    <Typography sx={deviceChangeToastTextStyles} variant="regular12">
      {message}
    </Typography>
    <IconButton
      data-testid={AudioDeviceSettingsTestIds.TOAST_CLOSE}
      size="small"
      sx={deviceChangeToastCloseStyles}
      onClick={onClose}
    >
      <CancelIcon size={IconSize[16]} style={deviceChangeToastIconStyles} />
    </IconButton>
  </Box>
));

DeviceChangeToast.displayName = 'DeviceChangeToast';

DeviceChangeToast.propTypes = {
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

const VoicemailGreetingSelector = memo(() => {
  const dispatch = useDispatch();
  const activeGreetingId = useSelector(selectActiveGreetingId);
  const isGreetingLoading = useSelector(selectIsGreetingLoading);
  const isGreetingSaving = useSelector(selectIsGreetingSaving);
  const voicemailRecordings = useSelector(selectVoiceMailAudioRecordings);

  useEffectSafeStrictMode(() => {
    dispatch(fetchActiveGreetingAction());
    dispatch(getAudioRecordingsListAction());
  }, [dispatch]);

  const handleGreetingChange = useCallback(
    e => {
      const { value } = e.target;

      if (value === 'none') {
        dispatch(removeActiveGreetingAction());

        return;
      }

      dispatch(setActiveGreetingAction({ audioRecordingId: value }));
    },
    [dispatch],
  );

  const selectValue = activeGreetingId ?? 'none';
  const disabled = isGreetingLoading || isGreetingSaving;

  return (
    <Box sx={deviceSectionStyles}>
      <Typography sx={deviceLabelStyles} variant="bold12">
        Voicemail Greeting
      </Typography>
      {isGreetingLoading ? (
        <CircularProgress size={20} />
      ) : (
        <>
          <Select
            native
            data-testid={AudioDeviceSettingsTestIds.GREETING_SELECT}
            disabled={disabled}
            size="small"
            sx={disabled ? disabledSelectStyles : selectStyles}
            value={selectValue}
            onChange={handleGreetingChange}
          >
            <option style={greetingNoneOptionStyles} value="none">
              None (use system default)
            </option>
            {voicemailRecordings.map(recording => (
              <option key={recording.audioRecordingId} value={recording.audioRecordingId}>
                {recording.name}
              </option>
            ))}
          </Select>
          <Typography
            component="a"
            href="/audio-recordings"
            sx={greetingManageLinkStyles}
            variant="regular12"
          >
            Manage Recordings
          </Typography>
        </>
      )}
    </Box>
  );
});

VoicemailGreetingSelector.displayName = 'VoicemailGreetingSelector';

// #endregion

const AudioDeviceSettings = ({ onBack }) => {
  const dispatch = useDispatch();
  const selectedInputDeviceId = useSelector(selectSelectedInputDeviceId);
  const selectedOutputDeviceId = useSelector(selectSelectedOutputDeviceId);
  const availableInputDevices = useSelector(selectAvailableInputDevices);
  const availableOutputDevices = useSelector(selectAvailableOutputDevices);
  const permissionState = useSelector(selectAudioPermissionState);

  const [isLoading, setIsLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState(null);
  const toastTimerRef = useRef(null);
  const permissionListenerRef = useRef(null);
  const prevDeviceCountRef = useRef(null);

  // Refs to track selected device IDs inside the devicechange handler
  // without adding them to the useEffect dependency array.
  const selectedInputDeviceIdRef = useRef(selectedInputDeviceId);
  const selectedOutputDeviceIdRef = useRef(selectedOutputDeviceId);

  selectedInputDeviceIdRef.current = selectedInputDeviceId;
  selectedOutputDeviceIdRef.current = selectedOutputDeviceId;

  const inputDeviceMissing = useMemo(
    () =>
      selectedInputDeviceId &&
      selectedInputDeviceId !== SYSTEM_DEFAULT_DEVICE_ID &&
      availableInputDevices.length > 0 &&
      !availableInputDevices.some(d => d.deviceId === selectedInputDeviceId),
    [selectedInputDeviceId, availableInputDevices],
  );

  const outputDeviceMissing = useMemo(
    () =>
      selectedOutputDeviceId &&
      selectedOutputDeviceId !== SYSTEM_DEFAULT_DEVICE_ID &&
      availableOutputDevices.length > 0 &&
      !availableOutputDevices.some(d => d.deviceId === selectedOutputDeviceId),
    [selectedOutputDeviceId, availableOutputDevices],
  );

  const micPermissionBlocked = permissionState === 'prompt' || permissionState === 'denied';

  const showToast = useCallback(msg => {
    setToastMessage(msg);

    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    toastTimerRef.current = setTimeout(() => {
      setToastMessage(null);
      toastTimerRef.current = null;
    }, TOAST_AUTO_DISMISS_MS);
  }, []);

  const dismissToast = useCallback(() => {
    setToastMessage(null);

    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);

      try {
        try {
          const permission = await navigator.permissions.query({
            name: 'microphone',
          });

          dispatch(setAudioPermissionStateAction(permission.state));

          const onPermissionChange = () => {
            dispatch(setAudioPermissionStateAction(permission.state));

            if (permission.state === 'granted') {
              enumerateDevices().then(({ inputDevices, outputDevices }) => {
                dispatch(
                  setAvailableAudioDevicesAction({
                    inputDevices,
                    outputDevices,
                  }),
                );
              });
            }
          };

          permission.addEventListener('change', onPermissionChange);
          permissionListenerRef.current = {
            permission,
            handler: onPermissionChange,
          };
        } catch {
          // permissions API not supported — proceed with enumeration
        }

        // Always enumerate — output devices don't need mic permission
        const { inputDevices, outputDevices } = await enumerateDevices();

        dispatch(setAvailableAudioDevicesAction({ inputDevices, outputDevices }));
        prevDeviceCountRef.current = inputDevices.length + outputDevices.length;

        // Restore persisted selections.
        // If Redux already holds a non-null value (same page-load, user just
        // closed / reopened settings), trust it and skip localStorage restore.
        const persisted = getPersistedAudioSettings();

        if (!selectedInputDeviceIdRef.current && persisted.selectedInputDeviceId) {
          // When the device list is empty (e.g. mic permission not yet granted),
          // keep the persisted value so it can be applied once devices appear.
          const stillExists =
            inputDevices.length === 0 ||
            inputDevices.some(d => d.deviceId === persisted.selectedInputDeviceId);
          const inputId = stillExists ? persisted.selectedInputDeviceId : null;

          dispatch(setSelectedInputDeviceAction(inputId));
        }

        if (!selectedOutputDeviceIdRef.current && persisted.selectedOutputDeviceId) {
          const stillExists =
            outputDevices.length === 0 ||
            outputDevices.some(d => d.deviceId === persisted.selectedOutputDeviceId);
          const outputId = stillExists ? persisted.selectedOutputDeviceId : null;

          dispatch(setSelectedOutputDeviceAction(outputId));
        }
      } finally {
        setIsLoading(false);
      }
    };

    initialize();

    const handleDeviceChange = async () => {
      const { inputDevices, outputDevices } = await enumerateDevices();

      dispatch(setAvailableAudioDevicesAction({ inputDevices, outputDevices }));

      const newCount = inputDevices.length + outputDevices.length;
      const prevCount = prevDeviceCountRef.current;

      if (prevCount !== null && newCount > prevCount) {
        showToast('New audio device connected.');
      }

      if (prevCount !== null && newCount < prevCount) {
        showToast('Audio device disconnected.');
      }

      prevDeviceCountRef.current = newCount;

      // Auto-fallback: if the currently selected device disappeared, reset to system default
      const currentInput = selectedInputDeviceIdRef.current;
      const currentOutput = selectedOutputDeviceIdRef.current;

      if (
        currentInput &&
        currentInput !== SYSTEM_DEFAULT_DEVICE_ID &&
        !inputDevices.some(d => d.deviceId === currentInput)
      ) {
        dispatch(setSelectedInputDeviceAction(null));
        persistAudioSettings({ selectedInputDeviceId: null });
        showToast('Selected microphone disconnected — switched to default.');
      }

      if (
        currentOutput &&
        currentOutput !== SYSTEM_DEFAULT_DEVICE_ID &&
        !outputDevices.some(d => d.deviceId === currentOutput)
      ) {
        dispatch(setSelectedOutputDeviceAction(null));
        persistAudioSettings({ selectedOutputDeviceId: null });
        showToast('Selected speaker disconnected — switched to default.');
      }
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);

      if (permissionListenerRef.current) {
        const { permission, handler } = permissionListenerRef.current;

        permission.removeEventListener('change', handler);
        permissionListenerRef.current = null;
      }

      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, [dispatch, showToast]);

  const onSelectInputDevice = useCallback(
    deviceId => {
      const normalizedId = deviceId === SYSTEM_DEFAULT_DEVICE_ID ? null : deviceId;

      dispatch(setSelectedInputDeviceAction(normalizedId));
      persistAudioSettings({ selectedInputDeviceId: normalizedId });

      const { currentCall } = telnyxService;

      if (currentCall && normalizedId) {
        currentCall.setAudioInDevice(normalizedId);
      }
    },
    [dispatch],
  );

  const onSelectOutputDevice = useCallback(
    deviceId => {
      const normalizedId = deviceId === SYSTEM_DEFAULT_DEVICE_ID ? null : deviceId;

      dispatch(setSelectedOutputDeviceAction(normalizedId));
      persistAudioSettings({ selectedOutputDeviceId: normalizedId });

      const { client } = telnyxService;

      if (client && normalizedId) {
        client.speaker = normalizedId;
      }
    },
    [dispatch],
  );

  const [isTesting, setIsTesting] = useState(false);
  const [isTestingMic, setIsTestingMic] = useState(false);
  const testToneRef = useRef(null);

  const onStopTestSpeaker = useCallback(() => {
    if (testToneRef.current) {
      const { oscillator1, oscillator2, ctx, audio } = testToneRef.current;

      oscillator1.stop();
      oscillator2.stop();
      ctx.close();
      audio.pause();
      audio.srcObject = null;
      testToneRef.current = null;
    }

    setIsTesting(false);
  }, []);

  const onTestSpeaker = useCallback(() => {
    if (isTesting) {
      onStopTestSpeaker();

      return;
    }

    const ctx = new AudioContext();
    const gainNode = ctx.createGain();
    const dest = ctx.createMediaStreamDestination();

    gainNode.gain.value = TEST_TONE_GAIN;

    // Create a pleasant two-tone chord (A4 + C#5) for a richer sound
    const oscillator1 = ctx.createOscillator();

    oscillator1.type = 'sine';
    oscillator1.frequency.value = TEST_TONE_BASE_FREQUENCY;
    oscillator1.connect(gainNode);
    oscillator1.start();

    const oscillator2 = ctx.createOscillator();

    oscillator2.type = 'sine';
    oscillator2.frequency.value = TEST_TONE_CHORD_FREQUENCY;
    oscillator2.connect(gainNode);
    oscillator2.start();

    gainNode.connect(dest);

    const audio = new Audio();

    audio.srcObject = dest.stream;
    audio.loop = true;

    const deviceId = selectedOutputDeviceId || SYSTEM_DEFAULT_DEVICE_ID;
    const canRouteToDevice = _.isFunction(audio.setSinkId) && deviceId !== SYSTEM_DEFAULT_DEVICE_ID;

    const playAudio = canRouteToDevice
      ? audio.setSinkId(deviceId).then(() => audio.play())
      : audio.play();

    if (playAudio && _.isFunction(playAudio.catch)) {
      playAudio.catch(_.noop);
    }

    testToneRef.current = { oscillator1, oscillator2, ctx, audio };
    setIsTesting(true);
  }, [isTesting, onStopTestSpeaker, selectedOutputDeviceId]);

  // Stop test tone on unmount
  useEffect(
    () => () => {
      if (testToneRef.current) {
        const { oscillator1, oscillator2, ctx, audio } = testToneRef.current;

        oscillator1.stop();
        oscillator2.stop();
        ctx.close();
        audio.pause();
        audio.srcObject = null;
        testToneRef.current = null;
      }
    },
    [],
  );

  const onRequestPermission = useCallback(() => {
    requestMicrophonePermission(dispatch);
  }, [dispatch]);

  const onToggleTestMic = useCallback(() => {
    setIsTestingMic(prev => !prev);
  }, []);

  if (isLoading) {
    return (
      <Box sx={settingsContainerStyles}>
        <Box sx={settingsHeaderStyles}>
          <IconButton
            data-testid={AudioDeviceSettingsTestIds.BACK}
            sx={backButtonStyles}
            onClick={onBack}
          >
            <ArrowIcon direction={IconDirection.LEFT} />
          </IconButton>
          <Typography sx={settingsTitleStyles} variant="bold16">
            Audio Settings
          </Typography>
        </Box>
        <Box sx={loadingContainerStyles}>
          <CircularProgress size={28} />
          <Typography sx={loadingTextStyles} variant="regular14">
            Detecting audio devices...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={settingsContainerStyles}>
      <Box sx={settingsHeaderStyles}>
        <IconButton
          data-testid={AudioDeviceSettingsTestIds.BACK}
          sx={backButtonStyles}
          onClick={onBack}
        >
          <ArrowIcon direction={IconDirection.LEFT} />
        </IconButton>
        <Typography sx={settingsTitleStyles} variant="bold16">
          Audio Settings
        </Typography>
      </Box>

      <Box sx={settingsBodyStyles}>
        <Box sx={deviceSectionStyles}>
          <Typography sx={deviceLabelStyles} variant="bold12">
            Speaker (Output)
          </Typography>
          <DeviceSelect
            devices={availableOutputDevices}
            value={selectedOutputDeviceId}
            onChange={onSelectOutputDevice}
          />
          {outputDeviceMissing && <FallbackNotice />}
          <Button
            data-testid={AudioDeviceSettingsTestIds.TEST_SPEAKER}
            sx={testButtonStyles}
            variant="outlined"
            onClick={onTestSpeaker}
          >
            {isTesting ? 'Stop Test' : 'Test Speaker'}
          </Button>
        </Box>

        <Box sx={deviceSectionStyles}>
          <Typography sx={deviceLabelStyles} variant="bold12">
            Microphone (Input)
          </Typography>
          <DeviceSelect
            devices={availableInputDevices}
            disabled={micPermissionBlocked}
            value={selectedInputDeviceId}
            onChange={onSelectInputDevice}
          />
          {inputDeviceMissing && <FallbackNotice />}
          {permissionState === 'prompt' && (
            <InlinePermissionPrompt onRequest={onRequestPermission} />
          )}
          {permissionState === 'denied' && <InlineDeniedNotice />}
          {permissionState === 'granted' && (
            <>
              <Button
                data-testid={AudioDeviceSettingsTestIds.TEST_MIC}
                sx={testButtonStyles}
                variant="outlined"
                onClick={onToggleTestMic}
              >
                {isTestingMic ? 'Stop Test' : 'Test Mic'}
              </Button>
              {isTestingMic && <MicLevelIndicator deviceId={selectedInputDeviceId} />}
            </>
          )}
        </Box>

        <VoicemailGreetingSelector />
      </Box>

      {toastMessage && <DeviceChangeToast message={toastMessage} onClose={dismissToast} />}
    </Box>
  );
};

AudioDeviceSettings.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default memo(AudioDeviceSettings);
